import React, { useEffect, useRef, useState } from 'react';

import styles from '../berryChat.module.scss';

/** Berry's backend rejects longer messages (MAX_MESSAGE_LENGTH, 400 "Message
 * too long"). The input enforces the same limit so that can't happen (B83). */
export const BERRY_MAX_MESSAGE_LENGTH = 500;
/** Show the counter once the customer is this close to the limit. */
const COUNTER_FROM = 400;
/** Matches `.chatInput { max-height }` in berryChat.module.scss. */
const INPUT_MAX_HEIGHT = 120;

interface ChatInputProps {
  onSend: (text: string) => void;
  disabled: boolean;
  /** Put a message back in the box after a failed send (B83: the text used
   * to be lost). `nonce` makes the same text restorable twice. */
  restore?: { text: string; nonce: number } | null;
}

const ChatInput: React.FC<ChatInputProps> = ({ onSend, disabled, restore }) => {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    // Include the border in the height, and only allow scrolling once the
    // box hits its max — a 2px shortfall showed a stray (green, site-styled)
    // scrollbar next to the input on every load (B82).
    el.style.height = 'auto';
    const border = el.offsetHeight - el.clientHeight;
    const wanted = el.scrollHeight + border;
    el.style.height = `${Math.min(wanted, INPUT_MAX_HEIGHT)}px`;
    el.style.overflowY = wanted > INPUT_MAX_HEIGHT ? 'auto' : 'hidden';
  }, [value]);

  useEffect(() => {
    if (!disabled) {
      textareaRef.current?.focus({ preventScroll: true });
    }
  }, [disabled]);

  useEffect(() => {
    if (restore?.text) setValue(restore.text.slice(0, BERRY_MAX_MESSAGE_LENGTH));
  }, [restore?.nonce]);

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled || trimmed.length > BERRY_MAX_MESSAGE_LENGTH) return;
    onSend(trimmed);
    setValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const showCounter = value.length >= COUNTER_FROM;

  return (
    <div className={styles.inputRow}>
      <textarea
        ref={textareaRef}
        className={styles.chatInput}
        rows={1}
        value={value}
        maxLength={BERRY_MAX_MESSAGE_LENGTH}
        onChange={(e) => setValue(e.target.value.slice(0, BERRY_MAX_MESSAGE_LENGTH))}
        onKeyDown={handleKeyDown}
        placeholder="Message Berry..."
        disabled={disabled}
        aria-label="Message to Berry"
        autoComplete="off"
      />
      {showCounter && (
        <span
          className={`${styles.inputCounter} ${
            value.length >= BERRY_MAX_MESSAGE_LENGTH ? styles.inputCounterOver : ''
          }`}
          aria-live="polite"
        >
          {value.length}/{BERRY_MAX_MESSAGE_LENGTH}
        </span>
      )}
      <button
        type="button"
        className={styles.sendButton}
        onClick={submit}
        disabled={disabled || !value.trim()}
        aria-label="Send message"
      >
        ↑
      </button>
    </div>
  );
};

export default ChatInput;
